import { resolveCmsAssetUrl } from "../../api/cmsClient";

export const POPUP_V2_BLOCK_TYPES = Object.freeze({
  TEXT: "text",
  IMAGE: "image",
  CTA: "cta",
  YOUTUBE: "youtube",
  COUNTDOWN: "countdown",
});

export const POPUP_V2_LAYOUTS = new Set([
  "centered",
  "media-top",
  "media-left",
  "media-right",
  "full-media",
]);
export const POPUP_V2_THEMES = new Set([
  "porcelain", "graphite", "plum", "linen", "slate",
  // Legacy aliases remain accepted for already-published campaigns.
  "brand", "light", "dark", "neutral",
]);
export const POPUP_V2_LAUNCHER_TONES = new Set(["auto", "light", "dark"]);
export const POPUP_V2_LAUNCHER_VISUALS = new Set(["icon", "logo", "none"]);
export const POPUP_V2_LAUNCHER_ICONS = new Set([
  "building", "graduation", "book", "calendar", "info", "megaphone", "globe", "sparkles",
]);
export const POPUP_V2_SIZES = new Set(["small", "medium", "large"]);
export const POPUP_V2_ALIGNMENTS = new Set(["left", "center"]);
export const POPUP_V2_MEDIA_MODES = new Set(["contained", "edge-to-edge"]);

const ALLOWED_RICH_TEXT_TAGS = new Set([
  "P", "BR", "STRONG", "B", "EM", "I", "U", "S", "UL", "OL", "LI", "BLOCKQUOTE", "A", "HR",
]);
const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

export const sanitizePopupV2Html = (value) => {
  const html = String(value || "");
  if (!html) return "";
  if (typeof DOMParser === "undefined") {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, "");
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";

  [...root.querySelectorAll("*")].forEach((element) => {
    if (!ALLOWED_RICH_TEXT_TAGS.has(element.tagName)) {
      element.replaceWith(...element.childNodes);
      return;
    }

    [...element.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (element.tagName === "A" && name === "href") {
        const href = attribute.value.trim();
        if (href.startsWith("/") && !href.startsWith("//")) return;
        try {
          const url = new URL(href, window.location.origin);
          if (ALLOWED_LINK_PROTOCOLS.has(url.protocol)) return;
        } catch (_error) {
          // Invalid href is removed below.
        }
      }
      element.removeAttribute(attribute.name);
    });
  });

  return root.innerHTML;
};

const firstValid = (...values) => values.find((value) => typeof value === "string" && value.trim());

export const getPopupV2MediaUrl = (media = {}) => {
  const variant = media?.variants?.medium || media?.variants?.original || media?.variants?.thumb;
  const candidate = firstValid(
    variant?.absoluteUrl,
    variant?.url,
    media?.absoluteUrl,
    media?.url,
  );
  return candidate ? resolveCmsAssetUrl(candidate) : "";
};

export const normalizePopupV2Presentation = (presentation = {}) => ({
  layout: POPUP_V2_LAYOUTS.has(presentation.layout) ? presentation.layout : "centered",
  theme: POPUP_V2_THEMES.has(presentation.theme) ? presentation.theme : "porcelain",
  size: POPUP_V2_SIZES.has(presentation.size) ? presentation.size : "medium",
  alignment: POPUP_V2_ALIGNMENTS.has(presentation.alignment) ? presentation.alignment : "center",
  mediaMode: POPUP_V2_MEDIA_MODES.has(presentation.mediaMode) ? presentation.mediaMode : "contained",
  launcherTone: POPUP_V2_LAUNCHER_TONES.has(presentation.launcherTone) ? presentation.launcherTone : "dark",
  launcherVisual: POPUP_V2_LAUNCHER_VISUALS.has(presentation.launcherVisual) ? presentation.launcherVisual : "icon",
  launcherIcon: POPUP_V2_LAUNCHER_ICONS.has(presentation.launcherIcon) ? presentation.launcherIcon : "building",
});

const DARK_LAUNCHER_THEMES = new Set(["graphite", "plum", "brand", "dark"]);
export const resolvePopupV2LauncherTone = (presentation = {}) => {
  if (presentation.launcherTone === "light" || presentation.launcherTone === "dark") return presentation.launcherTone;
  return DARK_LAUNCHER_THEMES.has(presentation.theme) ? "dark" : "light";
};

export const normalizePopupV2Behavior = (behavior = {}) => ({
  autoOpen: behavior.autoOpen !== false,
  showLauncher: behavior.showLauncher !== false,
  dismissPolicy: behavior.dismissPolicy === "session" ? "session" : "campaign",
});

export const normalizePopupV2Campaign = (campaign) => {
  if (!campaign || typeof campaign !== "object") return null;
  const id = Number(campaign.id);
  const revision = Number(campaign.revision);
  if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(revision) || revision <= 0) return null;

  const blocks = Array.isArray(campaign.blocks)
    ? campaign.blocks.filter((block) => block && typeof block === "object" && block.enabled !== false)
    : [];

  if (!blocks.length) return null;

  return {
    ...campaign,
    id,
    revision,
    launcherLabel: typeof campaign.launcherLabel === "string" ? campaign.launcherLabel.trim() : "",
    launcherSubtitle: typeof campaign.launcherSubtitle === "string" ? campaign.launcherSubtitle.trim() : "",
    accessibilityLabel: typeof campaign.accessibilityLabel === "string" ? campaign.accessibilityLabel.trim() : "",
    presentation: normalizePopupV2Presentation(campaign.presentation),
    behavior: normalizePopupV2Behavior(campaign.behavior),
    blocks,
  };
};

export const getPopupV2CountdownState = (block, now = Date.now()) => {
  const target = Date.parse(block?.targetAt || "");
  if (!Number.isFinite(target)) return { valid: false, expired: false, remaining: 0 };
  const remaining = Math.max(0, target - now);
  return { valid: true, expired: target <= now, remaining };
};

export const getVisiblePopupV2Blocks = (blocks = [], now = Date.now()) => {
  let hideCampaign = false;
  const visibleBlocks = [];

  for (const block of blocks) {
    if (!block || block.enabled === false) continue;
    if (block.type !== POPUP_V2_BLOCK_TYPES.COUNTDOWN) {
      visibleBlocks.push(block);
      continue;
    }

    const state = getPopupV2CountdownState(block, now);
    if (!state.valid || !state.expired || block.expiryBehavior === "continue") {
      visibleBlocks.push({ ...block, expired: state.expired });
      continue;
    }
    if (block.expiryBehavior === "hide-campaign") {
      hideCampaign = true;
      break;
    }
    // hide-block is intentionally omitted.
  }

  return { hideCampaign, blocks: visibleBlocks };
};

export const resolvePopupV2Layout = (layout, blocks = []) => {
  if (layout === "centered") return { layout: "centered", media: null, content: blocks };
  const mediaIndex = blocks.findIndex(
    (block) => block?.type === POPUP_V2_BLOCK_TYPES.IMAGE || block?.type === POPUP_V2_BLOCK_TYPES.YOUTUBE,
  );
  if (mediaIndex < 0) return { layout: "centered", media: null, content: blocks };
  return {
    layout,
    media: blocks[mediaIndex],
    content: blocks.filter((_, index) => index !== mediaIndex),
  };
};
